import React, { useEffect } from 'react';
import { Outlet, NavLink, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { useOrgStore } from '../store/org';
import { isOrgAdmin } from '../auth/capabilities';
import { DemoBanner, EnteringOverlay, OrgChip } from './VelopulseShell';
import AccountMenu from './AccountMenu';
import '../velopulse.css';

const TITLES = [
  [/^\/org-admin\/members/, '成員'],
  [/^\/org-admin\/settings/, '公司設定'],
  [/^\/org-admin/, '公司管理總覽'],
];
const titleOf = (path) => (TITLES.find(([re]) => re.test(path)) || [null, '公司管理'])[1];

/** 無權限時說明原因並提供返回，不靜默踢回工作台 */
export function NoAccessPage({ title = '沒有權限', detail } = {}) {
  const navigate = useNavigate();
  return (
    <div className="vp-standalone">
      <DemoBanner />
      <div className="vp-wrap">
        <h1>{title}</h1>
        <p className="vp-lead">{detail || '你沒有這個頁面的權限。此頁面僅限目前公司的公司管理員使用。'}</p>
        <div className="vp-wrap-actions">
          <button type="button" className="vp-btn" onClick={() => navigate('/')}>回工作台</button>
        </div>
      </div>
    </div>
  );
}

export function UsersToOrgAdminRedirect() {
  const { currentOrg, orgs, fetchOrgs } = useOrgStore();
  useEffect(() => { if (orgs === null) fetchOrgs(); }, [orgs, fetchOrgs]);
  if (!currentOrg) {
    if (orgs === null) return null;
    return <Navigate to="/orgs" replace />;
  }
  if (isOrgAdmin(currentOrg)) return <Navigate to="/org-admin/members" replace />;
  return <Navigate to="/no-access" replace />;
}

export function OrgAdminGate({ children }) {
  const { currentOrg, orgs, fetchOrgs } = useOrgStore();
  useEffect(() => { if (orgs === null) fetchOrgs(); }, [orgs, fetchOrgs]);
  if (!currentOrg) {
    if (orgs === null) return null;
    return <Navigate to="/orgs" replace />;
  }
  if (!isOrgAdmin(currentOrg)) {
    return <NoAccessPage title="沒有公司管理權限" detail="只有目前公司的公司管理員可以進入公司管理。切換公司後，權限會一併更換。" />;
  }
  return children;
}

export default function OrgAdminShell() {
  const { user, logout } = useAuthStore();
  const { currentOrg, orgs, fetchOrgs, clearOrg } = useOrgStore();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  useEffect(() => { if (orgs === null) fetchOrgs(); }, [orgs, fetchOrgs]);
  useEffect(() => { document.title = `Velopulse — ${titleOf(path)}`; }, [path]);

  const handleLogout = () => { clearOrg(); logout(); navigate('/login'); };
  const navCls = ({ isActive }) => (isActive ? 'active' : undefined);

  return (
    <div className="vp-root vp-admin">
      <aside className="vp-side">
        <div className="vp-brand">Velo<em>pulse</em></div>
        <div className="vp-admin-flag">公司管理{currentOrg?.name ? ` · ${currentOrg.short_name || currentOrg.name}` : ''}</div>
        <nav className="vp-nav" aria-label="公司管理導覽">
          <NavLink to="/org-admin" end className={navCls}>總覽</NavLink>
          <NavLink to="/org-admin/members" className={navCls}>成員</NavLink>
          <NavLink to="/org-admin/settings" className={navCls}>公司設定</NavLink>
        </nav>
        <div className="vp-side-foot">
          <Link to="/" className="vp-back-work">回工作台</Link>
          <button type="button" onClick={handleLogout}>登出（{user?.name || '使用者'}）</button>
        </div>
      </aside>

      <div className="vp-main">
        <DemoBanner />
        <header className="vp-top">
          <div className="vp-logo">公司管理</div>
          <div className="vp-crumb">{titleOf(path)}</div>
          <div className="vp-spacer" />
          <OrgChip />
          <AccountMenu />
        </header>
        <div className="vp-scroll"><Outlet /></div>
      </div>

      <nav className="vp-bottom vp-admin-bottom" aria-label="公司管理底部導覽">
        <NavLink to="/org-admin" end className={navCls}><span className="ico" aria-hidden="true">▣</span>總覽</NavLink>
        <NavLink to="/org-admin/members" className={navCls}><span className="ico" aria-hidden="true">👤</span>成員</NavLink>
        <NavLink to="/org-admin/settings" className={navCls}><span className="ico" aria-hidden="true">⚙</span>設定</NavLink>
        <NavLink to="/" className={navCls}><span className="ico" aria-hidden="true">↩</span>工作台</NavLink>
      </nav>
      <EnteringOverlay />
    </div>
  );
}

export function PlatformPlaceholderPage() {
  return (
    <NoAccessPage
      title="平台控制台尚未開放"
      detail="本版本不提供平台控制台與模擬登入。平台管理員入口將於後續獨立授權後開放。"
    />
  );
}
